import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { renderFlowchart, renderSequence } from 'diagramscript/browser';
import { tokens } from '../design-system/tokens';
import DOMPurify from 'dompurify';
// Inject diagram-specific CSS for SVG handling
const injectDiagramStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.querySelector('style[data-diagram-styles]')) return;

  const style = document.createElement('style');
  style.setAttribute('data-diagram-styles', 'true');
  style.textContent = `
    .diagram-svg-wrapper svg {
      display: block;
      max-width: 100%;
      height: auto !important;
    }
  `;
  document.head.appendChild(style);
};
injectDiagramStyles();

/**
 * Fullscreen Modal for diagrams
 */
function FullscreenModal({ svg, type, onClose }) {
  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return createPortal(
    <div style={styles.fullscreenOverlay} onClick={onClose}>
      <div style={styles.fullscreenHeader} onClick={(e) => e.stopPropagation()}>
        <span style={styles.fullscreenTitle}>
          {type === 'flowchart' ? 'Flowchart' : 'Sequence Diagram'}
        </span>
        <button style={styles.fullscreenCloseBtn} onClick={onClose}>
          ✕ Close (Esc)
        </button>
      </div>
      <div
        style={styles.fullscreenContent}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={styles.fullscreenSvg}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true }, ADD_TAGS: ['use'] }) }}
        />
      </div>
    </div>,
    document.body
  );
}

/**
 * DiagramRenderer - Renders flowcharts and sequence diagrams from JSON schemas
 *
 * Parses diagram JSON from chat messages and renders interactive SVG diagrams.
 * Supports both flowchart and sequence diagram types.
 */

/**
 * Parse diagram blocks from markdown content
 * Looks for ```diagram-flowchart or ```diagram-sequence code blocks
 */
export function extractDiagramBlocks(content) {
  if (!content) return { diagrams: [], cleanContent: content };

  const diagrams = [];
  let cleanContent = content;

  // Match diagram code blocks: ```diagram-flowchart or ```diagram-sequence
  const diagramRegex = /```diagram-(flowchart|sequence)\s*\n([\s\S]*?)```/g;

  let match;
  while ((match = diagramRegex.exec(content)) !== null) {
    const [fullMatch, diagramType, jsonContent] = match;

    try {
      const schema = JSON.parse(jsonContent.trim());
      diagrams.push({
        type: diagramType,
        schema,
        raw: jsonContent.trim(),
      });

      // Remove the diagram block entirely (don't leave a placeholder)
      cleanContent = cleanContent.replace(fullMatch, '');
    } catch (e) {
      console.warn('Failed to parse diagram JSON:', e);
      // Keep the original content if parsing fails
    }
  }

  // Also check for JSON blocks that look like diagrams (for backwards compatibility)
  // Match ```json, ``` (plain), or any code block containing diagram-like JSON
  const jsonBlockRegex = /```(?:json)?\s*\n(\{[\s\S]*?"(?:nodes|participants)"[\s\S]*?\})\s*```/g;

  while ((match = jsonBlockRegex.exec(content)) !== null) {
    const [fullMatch, jsonContent] = match;

    try {
      const schema = JSON.parse(jsonContent.trim());

      // Detect diagram type from schema structure
      let diagramType = null;
      if (schema.nodes && schema.edges) {
        diagramType = 'flowchart';
      } else if (schema.participants && schema.events) {
        diagramType = 'sequence';
      }

      if (diagramType) {
        diagrams.push({
          type: diagramType,
          schema,
          raw: jsonContent.trim(),
        });

        // Remove the diagram block entirely (don't leave a placeholder)
        cleanContent = cleanContent.replace(fullMatch, '');
      }
    } catch (e) {
      // Not valid JSON, skip
    }
  }

  // Clean up any extra whitespace from removed blocks
  cleanContent = cleanContent.replace(/\n{3,}/g, '\n\n').trim();

  return { diagrams, cleanContent };
}

/**
 * Single diagram component
 */
function DiagramView({ type, schema, onError }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const { svg, error } = useMemo(() => {
    try {
      // Validate schema before rendering to avoid crashes from malformed data
      if (type === 'sequence') {
        if (!Array.isArray(schema.participants) || schema.participants.some(p => p == null || (typeof p !== 'string' && typeof p.name !== 'string'))) {
          return { svg: null, error: 'Invalid sequence diagram: participants must be strings or have string names', warnings: [] };
        }
      } else if (type === 'flowchart') {
        if (!Array.isArray(schema.nodes) || !Array.isArray(schema.edges)) {
          return { svg: null, error: 'Invalid flowchart: missing nodes or edges', warnings: [] };
        }
      }

      // Apply dark theme for better visibility in dark UI
      const themedSchema = { ...schema, theme: schema.theme || 'dark' };

      // Normalize sequence diagrams: LLMs produce a simpler format than
      // what diagramscript expects.  Bridge the gap here.
      if (type === 'sequence') {
        // --- Participants ---
        // LLM:  { name, type? }  or plain string
        // Need: { id, label, type: 'actor' | 'participant' }
        const validTypes = new Set(['actor', 'participant']);
        if (Array.isArray(themedSchema.participants)) {
          themedSchema.participants = themedSchema.participants.map(p => {
            if (typeof p === 'string') return { id: p, label: p, type: 'participant' };
            const id = p.id || p.name || 'unknown';
            const label = p.label || p.name || id;
            const pType = validTypes.has(p.type) ? p.type : 'participant';
            return { id, label, type: pType };
          });
        }

        const knownIds = new Set((themedSchema.participants || []).map(p => p.id));

        // --- Events ---
        // LLM:  { from, to, message }
        // Need: { type: 'message', data: { from, to, label } }
        if (Array.isArray(themedSchema.events)) {
          themedSchema.events = themedSchema.events.map(evt => {
            // Already in typed format
            if (evt.type && evt.data) return evt;

            // Flat message format from LLM
            if (evt.from && evt.to) {
              const label = evt.message || evt.label || '';
              const from = evt.from;
              const to = evt.to;

              // Auto-add unknown participants referenced in events
              if (!knownIds.has(from)) {
                themedSchema.participants.push({ id: from, label: from, type: 'participant' });
                knownIds.add(from);
              }
              if (!knownIds.has(to)) {
                themedSchema.participants.push({ id: to, label: to, type: 'participant' });
                knownIds.add(to);
              }

              // Self-message
              if (from === to) {
                return { type: 'selfMessage', data: { participant: from, label } };
              }
              return { type: 'message', data: { from, to, label, dashed: !!evt.dashed } };
            }

            // Pass through unknown event shapes as-is
            return evt;
          });
        }
      }

      let svgContent;
      if (type === 'flowchart') {
        svgContent = renderFlowchart(themedSchema);
      } else if (type === 'sequence') {
        svgContent = renderSequence(themedSchema);
      } else {
        return { svg: null, error: `Unknown diagram type: ${type}`, warnings: [] };
      }

      return { svg: svgContent, error: null, warnings: [] };
    } catch (e) {
      console.error('Diagram render error:', e);
      return { svg: null, error: e.message, warnings: [] };
    }
  }, [type, schema]);

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorIcon}>!</div>
        <div style={styles.errorText}>
          Failed to render {type} diagram: {error}
        </div>
        <button
          style={styles.showJsonButton}
          onClick={() => setShowJson(!showJson)}
        >
          {showJson ? 'Hide' : 'Show'} JSON
        </button>
        {showJson && (
          <pre style={styles.jsonPreview}>
            {JSON.stringify(schema, null, 2)}
          </pre>
        )}
      </div>
    );
  }

  return (
    <>
      {isFullscreen && (
        <FullscreenModal
          svg={svg}
          type={type}
          onClose={handleCloseFullscreen}
        />
      )}
      <div style={styles.diagramContainer}>
        <div style={styles.diagramHeader}>
          <span style={styles.diagramType}>
            {type === 'flowchart' ? 'Flowchart' : 'Sequence Diagram'}
          </span>
          <div style={styles.diagramActions}>
            <button
              style={styles.actionButton}
              onClick={() => setIsFullscreen(true)}
              title="View fullscreen"
            >
              Fullscreen
            </button>
            <button
              style={styles.actionButton}
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </button>
            <button
              style={styles.actionButton}
              onClick={() => setShowJson(!showJson)}
              title={showJson ? 'Hide JSON' : 'Show JSON'}
            >
              {showJson ? 'Hide JSON' : 'JSON'}
            </button>
            <button
              style={styles.actionButton}
              onClick={() => {
                // Download SVG
                const blob = new Blob([svg], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${type}-diagram.svg`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              title="Download SVG"
            >
              Download
            </button>
          </div>
        </div>

        <div
          style={{
            ...styles.diagramContent,
            ...(isExpanded ? styles.diagramContentExpanded : {}),
          }}
          onClick={() => setIsFullscreen(true)}
          title="Click to view fullscreen"
        >
          <div
            className="diagram-svg-wrapper"
            style={styles.svgWrapper}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true }, ADD_TAGS: ['use'] }) }}
          />
        </div>

        {showJson && (
          <pre style={styles.jsonPreview}>
            {JSON.stringify(schema, null, 2)}
          </pre>
        )}
      </div>
    </>
  );
}

/**
 * Main DiagramRenderer component
 * Extracts and renders all diagrams from content
 */
function DiagramRenderer({ content }) {
  const { diagrams } = useMemo(() =>
    extractDiagramBlocks(content),
    [content]
  );

  if (diagrams.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      {diagrams.map((diagram, index) => (
        <DiagramView
          key={index}
          type={diagram.type}
          schema={diagram.schema}
        />
      ))}
    </div>
  );
}

/**
 * Utility to check if content contains diagram blocks
 */
export function hasDiagramBlocks(content) {
  if (!content) return false;

  // Check for explicit diagram blocks
  if (/```diagram-(flowchart|sequence)/i.test(content)) {
    return true;
  }

  // Check for JSON that looks like a diagram
  const jsonMatch = content.match(/```json\s*\n(\{[\s\S]*?\})\s*```/);
  if (jsonMatch) {
    try {
      const schema = JSON.parse(jsonMatch[1]);
      return (schema.nodes && schema.edges) || (schema.participants && schema.events);
    } catch {
      return false;
    }
  }

  return false;
}

const styles = {
  // Fullscreen modal styles
  fullscreenOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: tokens.colors.neutral[950],
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullscreenHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
    backgroundColor: tokens.colors.neutral[900],
    borderBottom: `1px solid ${tokens.colors.neutral[700]}`,
  },
  fullscreenTitle: {
    color: tokens.colors.primary[400],
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.medium,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  fullscreenCloseBtn: {
    padding: `${tokens.spacing[2]} ${tokens.spacing[5]}`,
    fontSize: tokens.typography.fontSize.base[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    border: `1px solid rgba(255, 255, 255, 0.3)`,
    borderRadius: tokens.borderRadius.md,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  fullscreenContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: tokens.spacing[8],
    paddingTop: '80px',
    overflow: 'auto',
    backgroundColor: tokens.colors.neutral[950],
  },
  fullscreenSvg: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
    maxHeight: '100%',
  },
  // Regular container styles
  container: {
    marginTop: tokens.spacing[3],
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[3],
  },
  diagramContainer: {
    backgroundColor: tokens.colors.neutral[900],
    borderRadius: tokens.borderRadius.lg,
    border: `1px solid ${tokens.colors.neutral[700]}`,
    overflow: 'hidden',
  },
  diagramHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
    backgroundColor: tokens.colors.neutral[950],
    borderBottom: `1px solid ${tokens.colors.neutral[700]}`,
  },
  diagramType: {
    color: tokens.colors.primary[400],
    fontSize: tokens.typography.fontSize.xs[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  diagramActions: {
    display: 'flex',
    gap: tokens.spacing[1],
  },
  actionButton: {
    padding: `4px 8px`,
    fontSize: '11px',
    color: tokens.colors.neutral[400],
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.colors.neutral[600]}`,
    borderRadius: tokens.borderRadius.sm,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  diagramContent: {
    padding: tokens.spacing[3],
    cursor: 'pointer',
    backgroundColor: tokens.colors.neutral[900],
  },
  diagramContentExpanded: {
    // No constraints when expanded
  },
  svgWrapper: {
    display: 'block',
    lineHeight: 0,
  },
  jsonPreview: {
    margin: 0,
    padding: tokens.spacing[3],
    backgroundColor: tokens.colors.neutral[950],
    borderTop: `1px solid ${tokens.colors.neutral[700]}`,
    color: tokens.colors.neutral[400],
    fontSize: tokens.typography.fontSize.xs[0],
    fontFamily: 'monospace',
    overflow: 'auto',
    maxHeight: '200px',
  },
  errorContainer: {
    padding: tokens.spacing[4],
    backgroundColor: 'rgba(210, 78, 38, 0.1)',
    borderRadius: tokens.borderRadius.lg,
    border: '1px solid rgba(210, 78, 38, 0.3)',
  },
  errorIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    backgroundColor: 'rgba(210, 78, 38, 0.2)',
    borderRadius: '50%',
    color: tokens.colors.error[500],
    fontWeight: 'bold',
    marginBottom: tokens.spacing[2],
  },
  errorText: {
    color: tokens.colors.error[500],
    fontSize: tokens.typography.fontSize.sm[0],
    marginBottom: tokens.spacing[2],
  },
  showJsonButton: {
    padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
    fontSize: tokens.typography.fontSize.xs[0],
    color: tokens.colors.neutral[400],
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.colors.neutral[600]}`,
    borderRadius: tokens.borderRadius.sm,
    cursor: 'pointer',
  },
};

export default DiagramRenderer;
